import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import ComputerIcon from '@material-ui/icons/Computer';
import ImportantDevicesIcon from '@material-ui/icons/ImportantDevices';
import CodeIcon from '@material-ui/icons/Code';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { useApp } from '@backstage/core-plugin-api';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="catalog?filters[user]=all" text="Home">
        <SidebarSubmenu title="Catalog">
          <SidebarSubmenuItem
            title="Domains"
            to="catalog?filters[kind]=domain&filters[user]=all"
            icon={useApp().getSystemIcon('kind:domain')}
          />
          <SidebarSubmenuItem
            title="Systems"
            to="catalog?filters[kind]=system&filters[user]=all"
            icon={useApp().getSystemIcon('kind:system')}
          />
          <SidebarSubmenuItem
            title="Components"
            to="catalog?filters[kind]=component&filters[user]=all"
            icon={useApp().getSystemIcon('kind:component')}
          />
          <SidebarSubmenuItem
            title="APIs"
            to="catalog?filters[kind]=api&filters[user]=all"
            icon={useApp().getSystemIcon('kind:api')}
          />
          <SidebarDivider />
          <SidebarSubmenuItem
            title="Resources"
            to="catalog?filters[kind]=resource&filters[user]=all"
            icon={useApp().getSystemIcon('kind:resource')}
          />
          <SidebarDivider />
          <SidebarSubmenuItem
            title="Groups"
            to="catalog?filters[kind]=group&filters[user]=all"
            icon={useApp().getSystemIcon('kind:group')}
          />
          <SidebarSubmenuItem
            title="Users"
            to="catalog?filters[kind]=usere&filters[user]=all"
            icon={useApp().getSystemIcon('kind:user')}
          />
        </SidebarSubmenu>
      </SidebarItem>
      <SidebarItem icon={ExtensionIcon} to="catalog?filters[user]=all&filters[kind]=api" text="APIs">
        <SidebarSubmenu title="Environments">
        <SidebarSubmenuItem
            title="Dev"
            to="catalog?filters[kind]=api&filters[user]=all&filters[lifecycles]=development"
            icon={useApp().getSystemIcon('kind:api')}
          />
          <SidebarSubmenuItem
            title="QA"
            to="catalog?filters[kind]=api&filters[user]=all&filters[lifecycles]=QA"
            icon={useApp().getSystemIcon('kind:api')}
          />
          <SidebarSubmenuItem
            title="Staging"
            to="catalog?filters[kind]=api&filters[user]=all&filters[lifecycles]=staging"
            icon={useApp().getSystemIcon('kind:api')}
          />
          <SidebarSubmenuItem
            title="Production"
            to="catalog?filters[kind]=api&filters[user]=all&filters[lifecycles]=production"
            icon={useApp().getSystemIcon('kind:api')}
          />
        </SidebarSubmenu>
      </SidebarItem>
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        {/* <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." /> */}
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
          <SidebarItem icon={CodeIcon} to="tech-radar" text="Tech Radar" />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
